import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Divider,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment,
  TableContainer,
  Skeleton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, TrendingUp, TrendingDown, Inventory as InventoryIcon, LocalShipping as ShippingIcon, Storefront as StoreIcon, BarChart as BarChartIcon, List as ListIcon, QrCode2 as QrCodeIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { supabase } from '../../../lib/supabase';
import dayjs from 'dayjs';
import DataService from '../../../services/dataService';
import { useAuth } from '../../../context/AuthContext';
import { useProducts } from '../../../context/ProductContext';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import EmptyState from '../../common/EmptyState';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  borderRadius: theme.shape.borderRadius * 2,
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const ProduceItem = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius * 2,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: active ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
    borderColor: active ? theme.palette.primary.main : theme.palette.action.hover,
  },
}));

const produceTypes = [
  { value: 'milk', label: 'Milk' },
  { value: 'eggs', label: 'Eggs' },
  { value: 'crop', label: 'Crop' },
  { value: 'livestock', label: 'Livestock' },
  { value: 'honey', label: 'Honey' },
  { value: 'other', label: 'Other' },
];

const defaultProduce = {
  type: '',
  name: '',
  unit: '',
  target: '',
};

const defaultRecord = {
  date: dayjs().format('YYYY-MM-DD'),
  quantity: '',
  unit_price: '',
  notes: '',
};

interface Batch {
  id: string;
  batch_code: string;
  produce_name: string;
  harvest_date: string;
  quantity: number;
  unit: string;
  quality_grade: string;
  status: string;
  notes?: string;
}

const FarmProduce = ({ farm }: { farm: any }) => {
  const theme = useTheme();
  const farmId = farm?.id;
  const [produceList, setProduceList] = useState<any[]>([]);
  const [selectedProduce, setSelectedProduce] = useState<any | null>(null);
  const [tabValue, setTabValue] = useState(0); // Inner tab for details
  const [mainTabValue, setMainTabValue] = useState(0); // Main tab (Overview vs Traceability)
  const [chartData, setChartData] = useState<any>(null);
  
  // Batch Traceability State
  const [batches, setBatches] = useState<Batch[]>([]);
  const [batchDialog, setBatchDialog] = useState(false);
  const [qrDialog, setQrDialog] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [batchForm, setBatchForm] = useState({
    produce_name: '',
    harvest_date: dayjs().format('YYYY-MM-DD'),
    quantity: '',
    unit: 'kg',
    quality_grade: 'Standard',
    notes: ''
  });






  const { user } = useAuth();
  const { refreshProducts } = useProducts();
  const navigate = useNavigate();
  const [marketModalOpen, setMarketModalOpen] = useState(false);
  const [marketForm, setMarketForm] = useState<any>(null);
  const [marketLoading, setMarketLoading] = useState(false);

  const handleOpenMarketModal = (produce: any) => {
    setMarketForm({
      name: produce.name,
      category: produce.type,
      price: '',
      unit: produce.unit,
      description: '',
      stock: '',
      farm_id: farm.id,
      seller_id: user?.id || '',
      shipping_method: 'self',
      shipping_fee: '',
      logistics_provider_id: '',
    });
    setMarketPhotoFile(null);
    setMarketPhotoPreview(null);
    setMarketModalOpen(true);
  };
  const handleCloseMarketModal = () => setMarketModalOpen(false);
  const handleMarketFormChange = (e: any) => setMarketForm({ ...marketForm, [e.target.name]: e.target.value });
  const handleSubmitMarketProduct = async () => {
    setMarketLoading(true);
    try {
      if (!marketForm?.name || !marketForm?.unit || !marketForm?.price || !marketForm?.stock) {
        setSnackbar({ open: true, message: 'Please fill in name, unit, price and stock', severity: 'error' });
        setMarketLoading(false);
        return;
      }
      let finalDescription = marketForm.description;
      if (marketForm.shipping_method === 'self') {
         finalDescription += `\n\n[Logistics: Self-Transport | Fee: ${marketForm.shipping_fee || 0}]`;
      } else if (marketForm.shipping_method === 'logistics') {
         const provider = logisticsProviders.find(p => p.id === marketForm.logistics_provider_id);
         finalDescription += `\n\n[Logistics: Partner | Provider: ${provider?.name || 'Any'}]`;
      }

      let imageUrl: string | null = null;
      if (marketPhotoFile) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const ext = marketPhotoFile.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2, 12)}_${Date.now()}.${ext}`;
            const filePath = `${user.id}/${fileName}`;

            const { error: uploadError } = await supabase.storage
              .from('farm-images')
              .upload(filePath, marketPhotoFile);

            if (uploadError) {
              console.error('Error uploading marketplace image:', uploadError);
            } else {
              const { data } = supabase.storage
                .from('farm-images')
                .getPublicUrl(filePath);
              imageUrl = data.publicUrl || null;
            }
          }
        } catch (uploadErr) {
          console.error('Error during marketplace image upload:', uploadErr);
        }
      }

      await DataService.createMarketplaceProduct({
        name: marketForm.name,
        category: marketForm.category,
        price: Number(marketForm.price),
        unit: marketForm.unit,
        description: finalDescription,
        stock: Number(marketForm.stock),
        farm_id: marketForm.farm_id,
        seller_id: marketForm.seller_id,
        images: imageUrl ? [imageUrl] : [],
      });
      
      // Update global product context so Marketplace reflects changes
      await refreshProducts();

      setSnackbar({ open: true, message: 'Product added to marketplace!', severity: 'success' });
      setMarketModalOpen(false);
      // Refresh list to update status
      const { data } = await supabase.from('marketplace').select('*').eq('farm_id', farmId);
      setMarketplaceProducts(data || []);
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.message || 'Failed to add product to marketplace', severity: 'error' });
    }
    setMarketLoading(false);
  };
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(defaultProduce);
  const [recordDialog, setRecordDialog] = useState(false);
  const [recordForm, setRecordForm] = useState(defaultRecord);
  const [records, setRecords] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [marketplaceProducts, setMarketplaceProducts] = useState<any[]>([]);
  const [logisticsProviders, setLogisticsProviders] = useState<any[]>([]);
  const [marketPhotoFile, setMarketPhotoFile] = useState<File | null>(null);
  const [marketPhotoPreview, setMarketPhotoPreview] = useState<string | null>(null);

  // Fetch produce list and marketplace status
  useEffect(() => {
    if (!farmId) return;
    setLoading(true);
    
    const fetchData = async () => {
      // Fetch Produce
      const { data: produceData, error: produceError } = await supabase
        .from('produce')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });

      if (produceError) {
        setSnackbar({ open: true, message: produceError.message, severity: 'error' });
      } else {
        setProduceList(produceData || []);
      }

      // Fetch Marketplace Products to check listing status
      const { data: marketData, error: marketError } = await supabase
        .from('marketplace')
        .select('*')
        .eq('farm_id', farmId);
      
      if (marketError) console.error('Error fetching market products:', marketError);
      setMarketplaceProducts(marketData || []);

      // Fetch Logistics Providers
      const providers = await DataService.getLogisticsProviders();
      setLogisticsProviders(providers || []);

      // Fetch Batches (Traceability)
      const { data: batchData } = await supabase
        .from('produce_batches')
        .select('*')
        .eq('farm_id', farmId)
        .order('harvest_date', { ascending: false });
      setBatches(batchData || []);

      setLoading(false);
    };

    fetchData();
  }, [farmId]);

  // Fetch records for selected produce
  useEffect(() => {
    if (!selectedProduce) return;
    supabase
      .from('daily_record')
      .select('*')
      .eq('produce_id', selectedProduce.id)
      .order('date', { ascending: false })
      .then(({ data }) => setRecords(data || []));
  }, [selectedProduce]);

  // Handlers for produce CRUD
  const handleOpenDialog = (produce?: any) => {
    if (produce) {
      setForm({
        ...produce,
        target: produce?.target === null || produce?.target === undefined ? '' : String(produce.target),
      });
      setSelectedProduce(produce);
    } else {
      setForm(defaultProduce);
      setSelectedProduce(null);
    }
    setOpenDialog(true);
  };
  const handleCloseDialog = () => setOpenDialog(false);
  const handleFormChange = (e: any) => setForm({ ...form, [e.target.name]: e.target.value });

  const parseOptionalNumber = (value: unknown) => {
    if (value === null || value === undefined) {
      return { value: null as number | null, error: null as string | null };
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
      return { value: null as number | null, error: null as string | null };
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      return { value: null as number | null, error: 'Please enter numbers only (0-9) or leave this field blank.' };
    }
    return { value: parsed, error: null as string | null };
  };

  const formatProduceSaveError = (error: any) => {
    const raw = String(error?.message ?? error ?? 'Failed to save produce.');
    const lower = raw.toLowerCase();
    if (lower.includes('invalid input syntax for type numeric')) {
      return 'Target must be a number (0-9). Leave it blank if you do not want to set a target.';
    }
    return raw;
  };

  const targetParse = useMemo(() => parseOptionalNumber(form.target), [form.target]);

  const handleSaveProduce = async () => {
    const name = String(form.name ?? '').trim();
    const type = String(form.type ?? '').trim();
    const unit = String(form.unit ?? '').trim();

    if (!name) {
      setSnackbar({ open: true, message: 'Please enter a produce name.', severity: 'error' });
      return;
    }
    if (!type) {
      setSnackbar({ open: true, message: 'Please select a produce type.', severity: 'error' });
      return;
    }
    if (!unit) {
      setSnackbar({ open: true, message: 'Please enter a unit (e.g., Kg, Liters).', severity: 'error' });
      return;
    }
    if (targetParse.error) {
      setSnackbar({ open: true, message: targetParse.error, severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type,
        name,
        unit,
        target: targetParse.value,
        updated_at: new Date().toISOString(),
      };

      if (selectedProduce) {
        const { error } = await supabase.from('produce').update(payload).eq('id', selectedProduce.id);
        if (error) {
          setSnackbar({ open: true, message: formatProduceSaveError(error), severity: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Produce updated!', severity: 'success' });
      } else {
        const { error } = await supabase.from('produce').insert([{ ...payload, farm_id: farmId }]);
        if (error) {
          setSnackbar({ open: true, message: formatProduceSaveError(error), severity: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Produce added!', severity: 'success' });
      }

      setOpenDialog(false);
      const { data } = await supabase
        .from('produce')
        .select('*')
        .eq('farm_id', farmId)
        .order('created_at', { ascending: false });
      setProduceList(data || []);
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteProduce = async (id: string) => {
    if (!window.confirm('Delete this produce type?')) return;
    setLoading(true);
    const { error } = await supabase.from('produce').delete().eq('id', id);
    if (error) setSnackbar({ open: true, message: error.message, severity: 'error' });
    else setSnackbar({ open: true, message: 'Produce deleted!', severity: 'success' });
    const { data } = await supabase.from('produce').select('*').eq('farm_id', farmId).order('created_at', { ascending: false });
    setProduceList(data || []);
    setLoading(false);
    setSelectedProduce(null);
  };

  // Handlers for daily record CRUD
  const handleOpenRecordDialog = (produce: any) => {
    setSelectedProduce(produce);
    setRecordForm({ ...defaultRecord });
    setRecordDialog(true);
  };
  const handleCloseRecordDialog = () => setRecordDialog(false);
  const handleRecordFormChange = (e: any) => setRecordForm({ ...recordForm, [e.target.name]: e.target.value });
  const handleSaveRecord = async () => {
    setLoading(true);
    const { error } = await supabase.from('daily_record').insert([
      {
        ...recordForm,
        produce_id: selectedProduce.id,
        quantity: Number(recordForm.quantity),
        unit_price: recordForm.unit_price ? Number(recordForm.unit_price) : null,
      },
    ]);
    if (error) setSnackbar({ open: true, message: error.message, severity: 'error' });
    else setSnackbar({ open: true, message: 'Record added!', severity: 'success' });
    setRecordDialog(false);
    // Refresh
    const { data } = await supabase.from('daily_record').select('*').eq('produce_id', selectedProduce.id).order('date', { ascending: false });
    setRecords(data || []);
    setLoading(false);
  };
  const handleDeleteRecord = async (id: string) => {
    if (!window.confirm('Delete this record?')) return;
    setLoading(true);
    const { error } = await supabase.from('daily_record').delete().eq('id', id);
    if (error) setSnackbar({ open: true, message: error.message, severity: 'error' });
    else setSnackbar({ open: true, message: 'Record deleted!', severity: 'success' });
    const { data } = await supabase.from('daily_record').select('*').eq('produce_id', selectedProduce.id).order('date', { ascending: false });
    setRecords(data || []);
    setLoading(false);
  };

  // Batch Handlers
  const handleOpenBatchDialog = () => {
    setBatchForm({
      produce_name: produceList.length > 0 ? produceList[0].name : '',
      harvest_date: dayjs().format('YYYY-MM-DD'),
      quantity: '',
      unit: produceList.length > 0 ? produceList[0].unit : 'kg',
      quality_grade: 'Standard',
      notes: ''
    });
    setBatchDialog(true);
  };

  const handleSaveBatch = async () => {
    setLoading(true);
    try {
      // Generate unique batch code: PROD-YYYYMMDD-RAND
      const dateStr = dayjs(batchForm.harvest_date).format('YYYYMMDD');
      const rand = Math.floor(1000 + Math.random() * 9000);
      const batchCode = `${batchForm.produce_name.substring(0, 3).toUpperCase()}-${dateStr}-${rand}`;

      const { error } = await supabase.from('produce_batches').insert({
        farm_id: farmId,
        produce_name: batchForm.produce_name,
        batch_code: batchCode,
        harvest_date: batchForm.harvest_date,
        quantity: Number(batchForm.quantity),
        unit: batchForm.unit,
        quality_grade: batchForm.quality_grade,
        notes: batchForm.notes,
        status: 'active'
      });

      if (error) throw error;

      // Log initial movement
      // First get the batch id (or just refresh)
      // For simplicity, just refresh list
      const { data } = await supabase
        .from('produce_batches')
        .select('*')
        .eq('farm_id', farmId)
        .order('harvest_date', { ascending: false });
      setBatches(data || []);

      setSnackbar({ open: true, message: 'Batch created successfully!', severity: 'success' });
      setBatchDialog(false);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewQr = (batch: Batch) => {
    setSelectedBatch(batch);
    setQrDialog(true);
  };

  const handleMarketPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setMarketPhotoFile(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setMarketPhotoPreview(previewUrl);
    } else {
      setMarketPhotoPreview(null);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Produce & Production</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your farm produce, track daily production, and list items on the marketplace.
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add Produce Type
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTabValue} onChange={(e, v) => setMainTabValue(v)}>
          <Tab label="Overview" icon={<InventoryIcon />} iconPosition="start" />
          <Tab label="Traceability & Batches" icon={<QrCodeIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {mainTabValue === 0 && (
        <Box>
          {loading ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StyledCard elevation={0} variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <InventoryIcon color="primary" /> Produce Types
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {[0, 1, 2, 3].map((index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1}>
                          <Skeleton variant="circular" width={32} height={32} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Skeleton width="70%" />
                            <Skeleton width="50%" />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              <Grid item xs={12} md={8}>
                <StyledCard elevation={0} variant="outlined">
                  <CardContent>
                    <Skeleton variant="text" width="40%" height={32} />
                    <Skeleton variant="text" width="60%" />
                    <Box sx={{ mt: 3 }}>
                      <Skeleton variant="rectangular" width="100%" height={240} />
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={3}>
          {/* Left Column: Produce List */}
          <Grid item xs={12} md={4}>
            <StyledCard elevation={0} variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InventoryIcon color="primary" /> Produce Types
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {produceList.length === 0 && (
                  <EmptyState 
                    title="No Produce" 
                    description="Add produce types to start tracking." 
                    icon={<InventoryIcon fontSize="large" color="disabled" />}
                  />
                )}

                <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', pr: 1 }}>
                  {produceList.map((prod) => {
                    const isListed = marketplaceProducts.some(mp => (mp.title || mp.name) === prod.name);
                    return (
                      <ProduceItem 
                        key={prod.id} 
                        active={selectedProduce?.id === prod.id}
                        onClick={() => setSelectedProduce(prod)}
                        elevation={selectedProduce?.id === prod.id ? 2 : 0}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {prod.name}
                            </Typography>
                            <Chip 
                              label={prod.type} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.65rem',
                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.main
                              }} 
                            />
                            {isListed && (
                              <Tooltip title="Listed on Marketplace">
                                <StoreIcon fontSize="small" color="primary" />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Unit: {prod.unit} {prod.target && `• Target: ${prod.target}`}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(prod);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </ProduceItem>
                    );
                  })}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Right Column: Details & Records */}
          <Grid item xs={12} md={8}>
            {selectedProduce ? (
              <Box>
                {/* Selected Produce Header Card */}
                <StyledCard elevation={0} variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                      <Box>
                        <Typography variant="h5" gutterBottom>{selectedProduce.name}</Typography>
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip label={selectedProduce.type} color="secondary" variant="outlined" size="small" />
                          <Typography variant="body2" color="text.secondary">
                            Unit: <b>{selectedProduce.unit}</b>
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          startIcon={<DeleteIcon />} 
                          onClick={() => handleDeleteProduce(selectedProduce.id)}
                        >
                          Delete
                        </Button>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          startIcon={<AddIcon />} 
                          onClick={() => handleOpenRecordDialog(selectedProduce)}
                        >
                          Log Production
                        </Button>
                        
                        {(() => {
                          const listing = marketplaceProducts.find(mp => (mp.title || mp.name) === selectedProduce.name);
                          const canDelist = listing && user && (user.role === 'admin' || listing.user_id === user.id);
                          return listing ? (
                            <>
                              <Chip 
                                icon={<StoreIcon />} 
                                label="Listed" 
                                color="success" 
                                variant="filled"
                                sx={{ height: 36.5, borderRadius: 1 }}
                              />
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => navigate(`/marketplace/product/${listing.id}`)}
                              >
                                View on Marketplace
                              </Button>
                              {canDelist && (
                                <Button
                                  variant="text"
                                  color="error"
                                  onClick={async () => {
                                    try {
                                      const { error } = await supabase
                                        .from('marketplace')
                                        .delete()
                                        .eq('id', listing.id);

                                      if (error) throw error;

                                      const { data: marketData } = await supabase
                                        .from('marketplace')
                                        .select('*')
                                        .eq('farm_id', farmId);

                                      setMarketplaceProducts(marketData || []);
                                      await refreshProducts();
                                      setSnackbar({ open: true, message: 'Product removed from marketplace.', severity: 'success' });
                                    } catch (err: any) {
                                      setSnackbar({ open: true, message: err.message || 'Failed to remove product from marketplace', severity: 'error' });
                                    }
                                  }}
                                >
                                  Remove from Marketplace
                                </Button>
                              )}
                            </>
                          ) : (
                          <Button 
                            variant="contained" 
                            color="success" 
                            startIcon={<StoreIcon />} 
                            onClick={() => handleOpenMarketModal(selectedProduce)}
                          >
                            List on Market
                          </Button>
                          );
                        })()}
                      </Box>
                    </Box>
                  </CardContent>
                </StyledCard>

                {/* Tabs for Chart/Table */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} aria-label="production tabs">
                    <Tab icon={<ListIcon />} iconPosition="start" label="Records" />
                    <Tab icon={<BarChartIcon />} iconPosition="start" label="Trends" />
                  </Tabs>
                </Box>

                {tabValue === 0 && (
                  <StyledCard elevation={0} variant="outlined">
                    <CardContent>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Quantity</TableCell>
                              <TableCell align="right">Unit Price</TableCell>
                              <TableCell>Notes</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {records.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                  <Typography color="text.secondary">No production records yet.</Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              records.map((rec) => (
                                <TableRow key={rec.id} hover>
                                  <TableCell>{dayjs(rec.date).format('MMM D, YYYY')}</TableCell>
                                  <TableCell align="right">
                                    <Typography fontWeight="medium">{rec.quantity} {selectedProduce.unit}</Typography>
                                  </TableCell>
                                  <TableCell align="right">{rec.unit_price ? `KES ${rec.unit_price}` : '-'}</TableCell>
                                  <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.notes || '-'}</TableCell>
                                  <TableCell align="right">
                                    <IconButton 
                                      size="small" 
                                      color="error" 
                                      onClick={() => handleDeleteRecord(rec.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </StyledCard>
                )}

                {tabValue === 1 && (
                  <StyledCard elevation={0} variant="outlined">
                    <CardContent>
                      {chartData ? (
                        <Box sx={{ height: 300 }}>
                          <Line 
                            data={chartData} 
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { display: false },
                                tooltip: {
                                  mode: 'index',
                                  intersect: false,
                                },
                              },
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  grid: { display: true, color: alpha(theme.palette.divider, 0.1) },
                                },
                                x: {
                                  grid: { display: false },
                                }
                              }
                            }} 
                          />
                        </Box>
                      ) : (
                        <EmptyState 
                          title="Not Enough Data" 
                          description="Log more production records to see trends." 
                          icon={<TrendingUp fontSize="large" color="disabled" />} 
                        />
                      )}
                    </CardContent>
                  </StyledCard>
                )}
              </Box>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight={400}
                component={Paper}
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 4, bgcolor: alpha(theme.palette.background.default, 0.5) }}
              >
                <InventoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Select a Produce Type</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select an item from the list to view details and records.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      </Box>
      )}

      {mainTabValue === 1 && (
         <Box>
           <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenBatchDialog}
              >
                Create New Batch
              </Button>
           </Box>

           <TableContainer component={Paper} variant="outlined">
             <Table>
               <TableHead>
                 <TableRow>
                   <TableCell>Batch Code</TableCell>
                   <TableCell>Produce</TableCell>
                   <TableCell>Harvest Date</TableCell>
                   <TableCell>Quantity</TableCell>
                   <TableCell>Quality</TableCell>
                   <TableCell>Status</TableCell>
                   <TableCell align="right">Actions</TableCell>
                 </TableRow>
               </TableHead>
               <TableBody>
                 {batches.length === 0 ? (
                   <TableRow>
                     <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                       <Typography color="text.secondary">No batches created yet. Create a batch to start tracking.</Typography>
                     </TableCell>
                   </TableRow>
                 ) : (
                   batches.map((batch) => (
                     <TableRow key={batch.id} hover>
                       <TableCell>
                         <Chip label={batch.batch_code} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                       </TableCell>
                       <TableCell>{batch.produce_name}</TableCell>
                       <TableCell>{dayjs(batch.harvest_date).format('MMM D, YYYY')}</TableCell>
                       <TableCell>{batch.quantity} {batch.unit}</TableCell>
                       <TableCell>
                         <Chip 
                           label={batch.quality_grade} 
                           size="small" 
                           color={batch.quality_grade === 'Premium' ? 'success' : batch.quality_grade === 'Standard' ? 'primary' : 'default'} 
                         />
                       </TableCell>
                       <TableCell>
                         <Chip label={batch.status} size="small" color="default" />
                       </TableCell>
                       <TableCell align="right">
                         <Button 
                           startIcon={<VisibilityIcon />} 
                           size="small" 
                           onClick={() => handleViewQr(batch)}
                         >
                           QR & Trace
                         </Button>
                       </TableCell>
                     </TableRow>
                   ))
                 )}
               </TableBody>
             </Table>
           </TableContainer>
         </Box>
      )}

      {/* Produce Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{selectedProduce ? 'Edit Produce' : 'Add Produce'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField 
              label="Name" 
              name="name" 
              value={form.name} 
              onChange={handleFormChange} 
              fullWidth 
              size="small"
              required
            />
            <TextField 
              select 
              label="Type" 
              name="type" 
              value={form.type} 
              onChange={handleFormChange} 
              fullWidth 
              size="small"
              required
            >
              {produceTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            <TextField label="Unit (e.g., Liters, Kg)" name="unit" value={form.unit} onChange={handleFormChange} fullWidth size="small" required />
            <TextField
              label="Target (Optional)"
              name="target"
              value={form.target}
              onChange={handleFormChange}
              fullWidth
              size="small"
              type="number"
              inputProps={{ inputMode: 'decimal' }}
              error={Boolean(targetParse.error)}
              helperText={targetParse.error ?? 'Daily target amount (leave blank if not needed)'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveProduce} variant="contained" disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Record Dialog */}
      <Dialog open={recordDialog} onClose={handleCloseRecordDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Log Production</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Typography variant="subtitle2" color="primary">{selectedProduce?.name}</Typography>
            <TextField type="date" label="Date" name="date" value={recordForm.date} onChange={handleRecordFormChange} fullWidth size="small" InputLabelProps={{ shrink: true }} />
            <TextField type="number" label={`Quantity (${selectedProduce?.unit})`} name="quantity" value={recordForm.quantity} onChange={handleRecordFormChange} fullWidth size="small" />
            <TextField type="number" label="Unit Price (Optional)" name="unit_price" value={recordForm.unit_price} onChange={handleRecordFormChange} fullWidth size="small" InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }} />
            <TextField label="Notes" name="notes" value={recordForm.notes} onChange={handleRecordFormChange} fullWidth size="small" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecordDialog}>Cancel</Button>
          <Button onClick={handleSaveRecord} variant="contained" disabled={loading}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Market Modal */}
      <Dialog open={marketModalOpen} onClose={handleCloseMarketModal} maxWidth="sm" fullWidth>
        <DialogTitle>List on Marketplace</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
             <TextField
               label="Product Name"
               name="name"
               value={marketForm?.name || ''}
               onChange={handleMarketFormChange}
               fullWidth
               size="small"
               required
               helperText="This will appear as the title of your product in the marketplace."
             />
             <Box>
               <Button
                 variant="outlined"
                 component="label"
                 fullWidth
               >
                 {marketPhotoFile ? 'Change Photo' : 'Upload Photo'}
                 <input
                   type="file"
                   accept="image/*"
                   hidden
                   onChange={handleMarketPhotoChange}
                 />
               </Button>
               {marketPhotoPreview && (
                 <Box mt={1} display="flex" justifyContent="center">
                   <Box
                     component="img"
                     src={marketPhotoPreview}
                     alt="Product preview"
                     sx={{ maxHeight: 180, borderRadius: 2, boxShadow: 2 }}
                   />
                 </Box>
               )}
             </Box>
             <Grid container spacing={2}>
               <Grid item xs={6}>
                 <TextField
                   type="number"
                   label="Your Price (KES)"
                   name="price"
                   value={marketForm?.price || ''}
                   onChange={handleMarketFormChange}
                   fullWidth
                   size="small"
                   required
                   helperText="The amount you want to receive per unit."
                 />
               </Grid>
               <Grid item xs={6}>
                 <TextField
                   type="number"
                   label="Stock Available"
                   name="stock"
                   value={marketForm?.stock || ''}
                   onChange={handleMarketFormChange}
                   fullWidth
                   size="small"
                   required
                   helperText="Total quantity currently available."
                 />
               </Grid>
             </Grid>

             {/* Price Breakdown Section */}
             {marketForm?.price && (
               <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderStyle: 'dashed' }}>
                 <Typography variant="subtitle2" gutterBottom color="primary.main" fontWeight="bold">
                   Marketplace Price Breakdown
                 </Typography>
                 <Box display="flex" justifyContent="space-between" mb={0.5}>
                   <Typography variant="body2">Your Price (per {marketForm.unit}):</Typography>
                   <Typography variant="body2" fontWeight="medium">KES {Number(marketForm.price).toLocaleString()}</Typography>
                 </Box>
                 <Box display="flex" justifyContent="space-between" mb={0.5}>
                   <Typography variant="body2">Platform Fee (10%):</Typography>
                   <Typography variant="body2" color="error.main">+ KES {(Number(marketForm.price) * 0.1).toLocaleString()}</Typography>
                 </Box>
                 <Divider sx={{ my: 1 }} />
                 <Box display="flex" justifyContent="space-between">
                   <Typography variant="subtitle1" fontWeight="bold">Final Customer Price:</Typography>
                   <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                     KES {(Number(marketForm.price) * 1.1).toLocaleString()}
                   </Typography>
                 </Box>
                 <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                   * Customers will see the final price. You will receive your price for every unit sold.
                 </Typography>
               </Paper>
             )}

             <TextField
               label="Description"
               name="description"
               value={marketForm?.description || ''}
               onChange={handleMarketFormChange}
               fullWidth
               multiline
               rows={3}
               size="small"
               helperText="Optional: include quality grade, variety, packaging, and other selling points."
             />
             
             <Typography variant="subtitle2" sx={{ mt: 1 }}>Logistics & Shipping</Typography>
             <FormControl fullWidth size="small">
               <InputLabel>Shipping Method</InputLabel>
               <Select name="shipping_method" value={marketForm?.shipping_method || 'self'} label="Shipping Method" onChange={handleMarketFormChange}>
                 <MenuItem value="self">Self-Transport (Farmer delivers)</MenuItem>
                 <MenuItem value="logistics">Partner Logistics</MenuItem>
               </Select>
             </FormControl>

             {marketForm?.shipping_method === 'self' ? (
               <TextField type="number" label="Delivery Fee (Optional)" name="shipping_fee" value={marketForm?.shipping_fee || ''} onChange={handleMarketFormChange} fullWidth size="small" helperText="Leave empty for free delivery" />
             ) : (
               <FormControl fullWidth size="small">
                 <InputLabel>Select Logistics Provider</InputLabel>
                 <Select name="logistics_provider_id" value={marketForm?.logistics_provider_id || ''} label="Select Logistics Provider" onChange={handleMarketFormChange}>
                   {logisticsProviders.map(p => (
                     <MenuItem key={p.id} value={p.id}>{p.name} ({p.vehicle_type}) - KES {p.base_rate}</MenuItem>
                   ))}
                 </Select>
               </FormControl>
             )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMarketModal}>Cancel</Button>
          <Button onClick={handleSubmitMarketProduct} variant="contained" disabled={marketLoading}>
            {marketLoading ? <CircularProgress size={24} /> : 'List Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Dialog */}
      <Dialog open={batchDialog} onClose={() => setBatchDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Batch</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth size="small">
              <InputLabel>Produce Type</InputLabel>
              <Select
                value={batchForm.produce_name}
                label="Produce Type"
                onChange={(e) => setBatchForm({ ...batchForm, produce_name: e.target.value, unit: produceList.find(p => p.name === e.target.value)?.unit || 'kg' })}
              >
                {produceList.map(p => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="Harvest Date"
              value={batchForm.harvest_date}
              onChange={(e) => setBatchForm({ ...batchForm, harvest_date: e.target.value })}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  type="number"
                  label={`Quantity (${batchForm.unit})`}
                  value={batchForm.quantity}
                  onChange={(e) => setBatchForm({ ...batchForm, quantity: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                 <FormControl fullWidth size="small">
                  <InputLabel>Quality Grade</InputLabel>
                  <Select
                    value={batchForm.quality_grade}
                    label="Quality Grade"
                    onChange={(e) => setBatchForm({ ...batchForm, quality_grade: e.target.value })}
                  >
                    <MenuItem value="Premium">Premium (Grade A)</MenuItem>
                    <MenuItem value="Standard">Standard (Grade B)</MenuItem>
                    <MenuItem value="Commercial">Commercial (Grade C)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Notes"
              value={batchForm.notes}
              onChange={(e) => setBatchForm({ ...batchForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveBatch} variant="contained" disabled={loading}>Create Batch</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Batch Traceability</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
             {selectedBatch && (
               <>
                 <Typography variant="h6" gutterBottom>{selectedBatch.produce_name}</Typography>
                 <Chip label={selectedBatch.batch_code} sx={{ fontFamily: 'monospace', mb: 2 }} />
                 
                 <Box sx={{ border: '1px solid #eee', p: 2, borderRadius: 2 }}>
                   <img 
                     src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://farmers-hub.com/trace/${selectedBatch.batch_code}`)}`} 
                     alt="Batch QR Code"
                     style={{ width: 200, height: 200 }} 
                   />
                 </Box>

                 <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                   Scan this code to view the full journey of this produce from harvest to market.
                 </Typography>

                 <Box width="100%" mt={2}>
                   <Typography variant="subtitle2" gutterBottom>Chain of Custody</Typography>
                   <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                      <Box display="flex" gap={2} mb={1}>
                        <Box sx={{ minWidth: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
                           <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">Harvested</Typography>
                          <Typography variant="caption" color="text.secondary">{dayjs(selectedBatch.harvest_date).format('MMM D, YYYY')}</Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={2}>
                        <Box sx={{ minWidth: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                           <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">Quality Graded: {selectedBatch.quality_grade}</Typography>
                          <Typography variant="caption" color="text.secondary">Ready for Packing</Typography>
                        </Box>
                      </Box>
                   </Paper>
                 </Box>
               </>
             )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialog(false)}>Close</Button>
          <Button variant="contained" startIcon={<ShippingIcon />}>Print Label</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default FarmProduce;
